import { motion } from "framer-motion";
import {
  MapPin,
  Building,
  BadgeCheck,
  Lock,
  BookCheck,
  Stethoscope,
  Archive,
  Globe2,
} from "lucide-react";
import AbstractShapes from "@/components/landing/AbstractShapes";

const BADGES = [
  { icon: Lock, label: "Open Access", href: null },
  { icon: BadgeCheck, label: "Peer Review", href: null },
  { icon: BookCheck, label: "Publication Ethics", href: null },
  { icon: Stethoscope, label: "Medical Research", href: null },
  {
    icon: Archive,
    label: "Archived in Portico",
    href: "https://www.portico.org/",
    badge: "PORTICO",
  },
  {
    icon: Globe2,
    label: "Indexed · cOAlition S",
    href: "https://www.coalition-s.org/",
    badge: "PLAN S",
  },
];

export default function CompanyInfo() {
  return (
    <section
      id="contact"
      data-testid="company-info-section"
      className="relative py-24 md:py-32 bg-white border-y border-[var(--sci-border)] overflow-hidden print:py-8 print:border-0"
    >
      <AbstractShapes
        variant="sigil"
        tone="ink"
        className="-right-24 -top-24 w-[420px] h-[420px] opacity-70"
      />
      <AbstractShapes
        variant="arcs"
        tone="ink"
        className="-left-20 bottom-0 w-[360px] h-[360px] opacity-60"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="overline text-[var(--sci-teal)] mb-5">
              · 11 / Publisher Information
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[var(--sci-navy)] tracking-tight leading-[1.02]">
              Registered. <br />
              Transparent. <br />
              <em className="not-italic text-[var(--sci-teal)]">Independent.</em>
            </h2>
            <p className="mt-6 text-[var(--sci-text-muted)] leading-relaxed">
              International Sci Ink Press Ltd is a registered academic publishing
              entity headquartered in Sofia, Bulgaria. Our journals are archived
              in Portico and indexed in line with cOAlition S / Plan S open
              access principles.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="grid sm:grid-cols-2 gap-px bg-[var(--sci-border)] border border-[var(--sci-border)]">
              <InfoCell
                icon={Building}
                label="Company"
                value="International Sci Ink Press Ltd"
              />
              <InfoCell
                icon={BadgeCheck}
                label="Registration No."
                value="205414288"
                mono
              />
              <InfoCell
                icon={MapPin}
                label="Headquarters"
                value={
                  <>
                    Antim I 57, fl. 1<br />
                    Vazrazhdane District<br />
                    Sofia 1303, Bulgaria
                  </>
                }
                spanFull
              />
            </div>

            {/* Trust badges */}
            <div className="mt-10">
              <div className="overline text-[var(--sci-text-muted)] mb-5">
                Trust · Standards · Indexation
              </div>
              <div className="flex flex-wrap gap-3">
                {BADGES.map((b, i) => {
                  const Icon = b.icon;
                  const Wrapper = b.href ? "a" : "div";
                  const wrapperProps = b.href
                    ? {
                        href: b.href,
                        target: "_blank",
                        rel: "noopener noreferrer",
                      }
                    : {};
                  return (
                    <Wrapper
                      key={b.label}
                      {...wrapperProps}
                      data-testid={`trust-badge-${i}`}
                      className="inline-flex items-center gap-2.5 border border-[var(--sci-border)] bg-white px-4 py-3 hover:border-[var(--sci-teal)] hover:text-[var(--sci-teal)] transition-colors group"
                    >
                      <Icon
                        size={16}
                        strokeWidth={1.5}
                        className="text-[var(--sci-teal)]"
                      />
                      <span className="text-sm text-[var(--sci-navy)] font-medium group-hover:text-[var(--sci-teal)]">
                        {b.label}
                      </span>
                      {b.badge && (
                        <span className="font-mono-sci text-[10px] tracking-[0.18em] text-[var(--sci-text-muted)] border-l border-[var(--sci-border)] pl-2.5 ml-1">
                          {b.badge}
                        </span>
                      )}
                    </Wrapper>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function InfoCell({ icon: Icon, label, value, href, mono, spanFull }) {
  const Wrapper = href ? "a" : "div";
  const target = href && href.startsWith("http") ? "_blank" : undefined;
  return (
    <Wrapper
      {...(href
        ? {
            href,
            target,
            rel: target ? "noopener noreferrer" : undefined,
            "data-testid": `info-cell-${label
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")}`,
          }
        : {
            "data-testid": `info-cell-${label
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")}`,
          })}
      className={`bg-white p-7 md:p-8 group hover:bg-[var(--sci-bg)] transition-colors block ${
        spanFull ? "sm:col-span-2" : ""
      }`}
    >
      <div className="flex items-center gap-3 mb-3">
        <Icon
          size={15}
          strokeWidth={1.5}
          className="text-[var(--sci-teal)]"
        />
        <span className="overline text-[var(--sci-text-muted)]">{label}</span>
      </div>
      <div
        className={`text-[var(--sci-navy)] text-lg md:text-xl ${
          mono ? "font-mono-sci tracking-wider" : "font-serif-display"
        } leading-snug group-hover:text-[var(--sci-teal)] transition-colors`}
      >
        {value}
      </div>
    </Wrapper>
  );
}
